import React, { useState, useMemo } from 'react';
import { Wallet, ClipboardList, Receipt, BarChart3, ChevronLeft, ChevronRight, ArrowLeft, Copy, Trash2, Plus, Zap, Download } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { formatNumber } from '../utils';

const MONTHS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const SECTIONS = [
  { key: 'income', es: 'INGRESOS', en: 'INCOME', color: 'emerald', isIncome: true },
  { key: 'essential', es: 'ESSENTIAL LIVING', en: 'ESSENTIAL LIVING', color: 'blue' },
  { key: 'family', es: 'FAMILY & GIVING', en: 'FAMILY & GIVING', color: 'amber' },
  { key: 'wealth', es: 'WEALTH BUILDING', en: 'WEALTH BUILDING', color: 'green' },
  { key: 'future', es: 'FUTURE GOALS', en: 'FUTURE GOALS', color: 'rose' },
  { key: 'lifestyle', es: 'LIFESTYLE', en: 'LIFESTYLE', color: 'purple' }
];

const DEFAULT_ROWS = {
  income: ['Nómina', 'Ingresos Extras'],
  essential: ['Alquiler', 'Servicios', 'Seguros', 'Supermercado', 'Transporte', 'Taxes'],
  family: ['Familia', 'Donación'],
  wealth: ['Ahorro', 'Inversiones'],
  future: ['Formación', 'Vacaciones', 'Extra (regalos)'],
  lifestyle: ['Ocio', 'Subscripciones', 'Ropa', 'Cuidado personal']
};

const COLORS = {
  emerald: { head: 'bg-emerald-50 text-emerald-700', sub: 'bg-emerald-50 text-emerald-700' },
  blue: { head: 'bg-blue-50 text-blue-700', sub: 'bg-blue-50 text-blue-700' },
  amber: { head: 'bg-amber-50 text-amber-700', sub: 'bg-amber-50 text-amber-700' },
  green: { head: 'bg-green-50 text-green-700', sub: 'bg-green-50 text-green-700' },
  rose: { head: 'bg-rose-50 text-rose-700', sub: 'bg-rose-50 text-rose-700' },
  purple: { head: 'bg-purple-50 text-purple-700', sub: 'bg-purple-50 text-purple-700' }
};

const emptyRow = (name) => ({ id: `r_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, name, values: Array(12).fill(0) });

export const defaultTable = () => {
  const t = {};
  SECTIONS.forEach(s => { t[s.key] = DEFAULT_ROWS[s.key].map(n => emptyRow(n)); });
  return t;
};

const rowTotal = (row) => row.values.reduce((a, b) => a + (Number(b) || 0), 0);
const sectionMonthSum = (rows, m) => rows.reduce((a, r) => a + (Number(r.values[m]) || 0), 0);

export function computeTotals(table) {
  const income = Array(12).fill(0);
  const expenses = Array(12).fill(0);
  SECTIONS.forEach(s => {
    const rows = table[s.key] || [];
    for (let m = 0; m < 12; m++) {
      const sum = sectionMonthSum(rows, m);
      if (s.isIncome) income[m] += sum; else expenses[m] += sum;
    }
  });
  return { income, expenses, balance: income.map((v, i) => v - expenses[i]) };
}

function BudgetGrid({ mode, table, onChange, year, confirmedExtractions, language }) {
  const months = language === 'es' ? MONTHS_ES : MONTHS_EN;
  const totals = useMemo(() => computeTotals(table), [table]);

  const update = (next) => onChange({ ...table, ...next });

  const setCell = (sKey, rowId, m, val) => {
    update({ [sKey]: table[sKey].map(r => r.id === rowId ? { ...r, values: r.values.map((v, i) => i === m ? val : v) } : r) });
  };
  const setName = (sKey, rowId, name) => {
    update({ [sKey]: table[sKey].map(r => r.id === rowId ? { ...r, name } : r) });
  };
  const copyRow = (sKey, rowId) => {
    update({ [sKey]: table[sKey].map(r => {
      if (r.id !== rowId) return r;
      const src = r.values.find(v => Number(v) > 0) || 0;
      return { ...r, values: Array(12).fill(Number(src)) };
    }) });
  };
  const addRow = (sKey) => update({ [sKey]: [...table[sKey], emptyRow(language === 'es' ? 'Nuevo concepto' : 'New item')] });
  const delRow = (sKey, rowId) => update({ [sKey]: table[sKey].filter(r => r.id !== rowId) });

  const importExtractions = (rowId) => {
    const byMonth = Array(12).fill(0);
    Object.values(confirmedExtractions || {}).forEach(ex => {
      if (!ex.date) return;
      const d = new Date(ex.date);
      if (!isNaN(d) && d.getFullYear() === year) byMonth[d.getMonth()] += ex.amount || 0;
    });
    update({ income: table.income.map(r => r.id === rowId ? { ...r, values: byMonth } : r) });
  };

  const cell = (sKey, row, m) => (
    <td key={m} className="px-1 py-1">
      <input
        type="text"
        inputMode="decimal"
        value={row.values[m] || ''}
        placeholder="0"
        onChange={(e) => setCell(sKey, row.id, m, Number(e.target.value.replace(/[^0-9.-]/g, '')) || 0)}
        className="w-16 px-1 py-1 text-right text-xs bg-gray-50 border border-transparent focus:border-blue-300 focus:bg-white rounded outline-none"
        data-testid={`fin-cell-${sKey}-${m}`}
      />
    </td>
  );

  const subtotalRow = (label, rows, cls) => (
    <tr className={`${cls} font-bold text-xs`}>
      <td className="px-3 py-2 uppercase">{label}</td>
      {months.map((_, m) => <td key={m} className="px-2 py-2 text-right">{formatNumber(sectionMonthSum(rows, m))}</td>)}
      <td className="px-3 py-2 text-right">${formatNumber(rows.reduce((a, r) => a + rowTotal(r), 0))}</td>
    </tr>
  );

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="text-gray-500 font-bold uppercase">
            <td className="px-3 py-3">Concepto</td>
            {months.map(m => <td key={m} className="px-2 py-3 text-right">{m}</td>)}
            <td className="px-3 py-3 text-right">{language === 'es' ? 'AÑO' : 'YEAR'}</td>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {SECTIONS.map(s => {
            const rows = table[s.key] || [];
            const c = COLORS[s.color];
            return (
              <React.Fragment key={s.key}>
                <tr className={c.head}>
                  <td colSpan={14} className="px-3 py-1.5 font-black text-[11px]">
                    {language === 'es' ? s.es : s.en}
                    <button onClick={() => addRow(s.key)} className="ml-2 align-middle hover:scale-110 transition-transform" data-testid={`fin-add-row-${s.key}`}>
                      <Plus size={14} className="inline" />
                    </button>
                  </td>
                </tr>
                {rows.map(row => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-3 py-1 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <input
                          value={row.name}
                          onChange={(e) => setName(s.key, row.id, e.target.value)}
                          className="w-28 sm:w-32 bg-transparent font-medium text-gray-700 outline-none focus:bg-blue-50 rounded px-1"
                        />
                        <button onClick={() => copyRow(s.key, row.id)} className="text-gray-300 hover:text-blue-500" title={language === 'es' ? 'Copiar a todos los meses' : 'Copy to all months'} data-testid={`fin-copy-${row.id}`}>
                          <Copy size={12} />
                        </button>
                        {mode === 'actual' && s.isIncome && row.name.toLowerCase().includes('extra') && (
                          <button onClick={() => importExtractions(row.id)} className="text-emerald-400 hover:text-emerald-600" title={language === 'es' ? 'Traer extracciones de trading' : 'Import trading extractions'} data-testid="fin-import-extractions">
                            <Zap size={13} />
                          </button>
                        )}
                        {!s.isIncome && (
                          <button onClick={() => delRow(s.key, row.id)} className="text-gray-300 hover:text-red-500" data-testid={`fin-del-${row.id}`}>
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                    {months.map((_, m) => cell(s.key, row, m))}
                    <td className="px-3 py-1 text-right font-bold text-gray-800">${formatNumber(rowTotal(row))}</td>
                  </tr>
                ))}
                {subtotalRow(s.isIncome ? (language === 'es' ? 'Total Ingresos' : 'Total Income') : 'Subtotal', rows, c.sub)}
              </React.Fragment>
            );
          })}
          <tr className="bg-gray-900 text-white font-black text-xs">
            <td className="px-3 py-2.5 uppercase">{language === 'es' ? 'Total General Gastos' : 'Total Expenses'}</td>
            {months.map((_, m) => <td key={m} className="px-2 py-2.5 text-right">{formatNumber(totals.expenses[m])}</td>)}
            <td className="px-3 py-2.5 text-right">${formatNumber(totals.expenses.reduce((a, b) => a + b, 0))}</td>
          </tr>
          <tr className="bg-emerald-50 font-black text-xs" data-testid="fin-balance-row">
            <td className="px-3 py-2.5 uppercase text-gray-800">{language === 'es' ? 'Balance (Ingresos − Gastos)' : 'Balance (Income − Expenses)'}</td>
            {months.map((_, m) => (
              <td key={m} className={`px-2 py-2.5 text-right ${totals.balance[m] >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{formatNumber(totals.balance[m])}</td>
            ))}
            <td className={`px-3 py-2.5 text-right ${totals.balance.reduce((a, b) => a + b, 0) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
              ${formatNumber(totals.balance.reduce((a, b) => a + b, 0))}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ResultsChart({ plan, real, language }) {
  const months = language === 'es' ? MONTHS_ES : MONTHS_EN;
  const [hover, setHover] = useState(null);
  const svgRef = React.useRef(null);
  const all = [...plan, ...real];
  const rawMax = Math.max(...all, 0);
  const rawMin = Math.min(...all, 0);
  const pad = (rawMax - rawMin) * 0.15 || 100;
  const top = rawMax + pad;
  const bottom = rawMin - pad;
  const W = 760, H = 230, L = 58, R = 14, T = 14, B = 28;
  const x = (i) => L + (i * (W - L - R)) / 11;
  const y = (v) => T + ((top - v) * (H - T - B)) / (top - bottom);
  const linePath = (arr) => arr.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const gridLines = [0, 0.25, 0.5, 0.75, 1].map(f => top - f * (top - bottom));

  const onMove = (e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * W;
    const i = Math.max(0, Math.min(11, Math.round(((relX - L) / (W - L - R)) * 11)));
    setHover({ i, left: e.clientX - rect.left, top: e.clientY - rect.top });
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 sm:p-5" data-testid="fin-results-chart">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="font-bold text-gray-800 text-sm sm:text-base">
          {language === 'es' ? 'Balance Plan vs Balance Real' : 'Plan Balance vs Real Balance'}
        </h3>
        <div className="flex items-center gap-4 text-xs font-bold">
          <span className="flex items-center gap-1.5 text-indigo-500">
            <span className="inline-block w-5 border-t-2 border-dashed border-indigo-500" /> {language === 'es' ? 'Plan' : 'Plan'}
          </span>
          <span className="flex items-center gap-1.5 text-emerald-600">
            <span className="inline-block w-5 border-t-2 border-emerald-600" /> {language === 'es' ? 'Real' : 'Real'}
          </span>
        </div>
      </div>
      <div className="relative">
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minHeight: 180 }} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
          {gridLines.map((v, i) => (
            <g key={i}>
              <line x1={L} y1={y(v)} x2={W - R} y2={y(v)} stroke="#f1f5f9" strokeWidth="1" />
              <text x={L - 6} y={y(v) + 3} textAnchor="end" fontSize="9" fill="#94a3b8">{`$${formatNumber(v)}`}</text>
            </g>
          ))}
          {bottom < 0 && top > 0 && (
            <line x1={L} y1={y(0)} x2={W - R} y2={y(0)} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3,3" />
          )}
          {hover && (
            <line x1={x(hover.i)} y1={T} x2={x(hover.i)} y2={H - B} stroke="#94a3b8" strokeWidth="1" strokeDasharray="4,3" />
          )}
          <path d={linePath(plan)} fill="none" stroke="#6366f1" strokeWidth="2" strokeDasharray="6,4" />
          <path d={linePath(real)} fill="none" stroke="#059669" strokeWidth="2.5" />
          {plan.map((v, i) => <circle key={`p${i}`} cx={x(i)} cy={y(v)} r={hover?.i === i ? 4 : 2.5} fill="#6366f1" />)}
          {real.map((v, i) => <circle key={`r${i}`} cx={x(i)} cy={y(v)} r={hover?.i === i ? 4.5 : 3} fill="#059669" />)}
          {months.map((m, i) => (
            <text key={m} x={x(i)} y={H - 8} textAnchor="middle" fontSize="9" fill="#64748b" fontWeight="bold">{m}</text>
          ))}
        </svg>
        {hover && (
          <div
            className="absolute pointer-events-none bg-gray-900/80 backdrop-blur-sm text-white rounded-lg px-3 py-2 text-[11px] shadow-xl z-10 whitespace-nowrap"
            style={{
              left: Math.min(hover.left + 12, (svgRef.current?.getBoundingClientRect().width || 400) - 150),
              top: Math.max(hover.top - 70, 0)
            }}
            data-testid="fin-chart-tooltip"
          >
            <p className="font-black mb-0.5">{months[hover.i]}</p>
            <p className="text-indigo-300">Plan: <span className="font-bold">${formatNumber(plan[hover.i])}</span></p>
            <p className="text-emerald-300">Real: <span className="font-bold">${formatNumber(real[hover.i])}</span></p>
            <p className={real[hover.i] - plan[hover.i] >= 0 ? 'text-emerald-400' : 'text-red-400'}>
              {language === 'es' ? 'Resultado' : 'Result'}: <span className="font-bold">{real[hover.i] - plan[hover.i] >= 0 ? '+' : ''}${formatNumber(real[hover.i] - plan[hover.i])}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function MonthDetail({ budget, actual, month, language, onBack, inline = false }) {
  const months = language === 'es' ? MONTHS_ES : MONTHS_EN;
  const sections = SECTIONS.map(s => {
    const bRows = budget[s.key] || [];
    const aRows = actual[s.key] || [];
    const names = [...new Set([...bRows.map(r => r.name), ...aRows.map(r => r.name)])];
    const rows = names.map(name => {
      const p = bRows.filter(r => r.name === name).reduce((a, r) => a + (Number(r.values[month]) || 0), 0);
      const re = aRows.filter(r => r.name === name).reduce((a, r) => a + (Number(r.values[month]) || 0), 0);
      return { name, plan: p, real: re, diff: re - p };
    }).filter(r => r.plan !== 0 || r.real !== 0);
    return { ...s, rows, planSum: sectionMonthSum(bRows, month), realSum: sectionMonthSum(aRows, month) };
  });
  const planBalance = sections.reduce((a, s) => a + (s.isIncome ? s.planSum : -s.planSum), 0);
  const realBalance = sections.reduce((a, s) => a + (s.isIncome ? s.realSum : -s.realSum), 0);
  const goodDiff = (s, diff) => (s.isIncome ? diff >= 0 : diff <= 0);

  return (
    <div className={`bg-white overflow-x-auto ${inline ? 'rounded-lg border border-indigo-100 shadow-sm' : 'rounded-xl shadow-lg border border-gray-100'}`} data-testid="fin-month-detail">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <h3 className="font-black text-gray-800 text-sm sm:text-base">
          {language === 'es' ? `Detalle de ${months[month]} por categorías` : `${months[month]} detail by category`}
        </h3>
        <button onClick={onBack} className="text-xs font-bold text-indigo-600 hover:text-indigo-800" data-testid="fin-detail-back">
          {inline ? (language === 'es' ? '✕ Cerrar' : '✕ Close') : (language === 'es' ? '← Vista general' : '← Overview')}
        </button>
      </div>
      <table className="min-w-full text-xs">
        <thead>
          <tr className="text-gray-500 font-bold uppercase bg-gray-50">
            <td className="px-3 py-2.5">{language === 'es' ? 'Categoría' : 'Category'}</td>
            <td className="px-3 py-2.5 text-right">Plan</td>
            <td className="px-3 py-2.5 text-right">Real</td>
            <td className="px-3 py-2.5 text-right">{language === 'es' ? 'Diferencia' : 'Difference'}</td>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sections.map(s => (
            <React.Fragment key={s.key}>
              <tr className={COLORS[s.color].head}>
                <td colSpan={4} className="px-3 py-1.5 font-black text-[11px]">{language === 'es' ? s.es : s.en}</td>
              </tr>
              {s.rows.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-1.5 text-gray-300 italic">{language === 'es' ? 'Sin movimientos' : 'No activity'}</td></tr>
              )}
              {s.rows.map(r => (
                <tr key={r.name} className="hover:bg-gray-50">
                  <td className="px-3 py-1.5 font-medium text-gray-700">{r.name}</td>
                  <td className="px-3 py-1.5 text-right text-gray-600">${formatNumber(r.plan)}</td>
                  <td className="px-3 py-1.5 text-right font-bold text-gray-800">${formatNumber(r.real)}</td>
                  <td className={`px-3 py-1.5 text-right font-bold ${goodDiff(s, r.diff) ? 'text-emerald-600' : 'text-red-600'}`}>
                    {r.diff >= 0 ? '+' : ''}${formatNumber(r.diff)}
                  </td>
                </tr>
              ))}
              <tr className={`${COLORS[s.color].sub} font-bold`}>
                <td className="px-3 py-1.5 uppercase">Subtotal</td>
                <td className="px-3 py-1.5 text-right">${formatNumber(s.planSum)}</td>
                <td className="px-3 py-1.5 text-right">${formatNumber(s.realSum)}</td>
                <td className={`px-3 py-1.5 text-right ${goodDiff(s, s.realSum - s.planSum) ? 'text-emerald-700' : 'text-red-600'}`}>
                  {s.realSum - s.planSum >= 0 ? '+' : ''}${formatNumber(s.realSum - s.planSum)}
                </td>
              </tr>
            </React.Fragment>
          ))}
          <tr className="bg-gray-900 text-white font-black">
            <td className="px-3 py-2.5 uppercase">{language === 'es' ? 'Balance del mes' : 'Month balance'}</td>
            <td className="px-3 py-2.5 text-right">${formatNumber(planBalance)}</td>
            <td className="px-3 py-2.5 text-right">${formatNumber(realBalance)}</td>
            <td className={`px-3 py-2.5 text-right ${realBalance - planBalance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {realBalance - planBalance >= 0 ? '+' : ''}${formatNumber(realBalance - planBalance)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function ResultsView({ budget, actual, language }) {
  const months = language === 'es' ? MONTHS_ES : MONTHS_EN;
  const plan = computeTotals(budget);
  const real = computeTotals(actual);
  const [selectedMonth, setSelectedMonth] = useState(null);
  let acc = 0;
  return (
    <div className="space-y-4">
      {/* Line chart: first thing you see */}
      <ResultsChart plan={plan.balance} real={real.balance} language={language} />

      {/* Month relation dropdown */}
      <div className="flex items-center gap-2 bg-white rounded-xl shadow-sm border border-gray-100 px-4 py-3">
        <label className="text-xs font-bold text-gray-500 uppercase whitespace-nowrap">
          {language === 'es' ? 'Relación por mes' : 'Monthly relation'}
        </label>
        <select
          value={selectedMonth === null ? '' : selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value === '' ? null : Number(e.target.value))}
          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-bold text-gray-700 bg-gray-50 outline-none focus:border-indigo-300"
          data-testid="fin-month-select"
        >
          <option value="">{language === 'es' ? 'General (todo el año)' : 'General (full year)'}</option>
          {months.map((m, i) => <option key={m} value={i}>{m}</option>)}
        </select>
        <span className="text-[10px] text-gray-400 hidden sm:inline">
          {language === 'es' ? 'o haz click en un mes de la tabla (otro click lo cierra)' : 'or click a month in the table (click again to close)'}
        </span>
      </div>

      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-x-auto" data-testid="fin-results-table">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-gray-500 font-bold uppercase bg-gray-50">
              <td className="px-3 py-3">{language === 'es' ? 'Mes' : 'Month'}</td>
              <td className="px-3 py-3 text-right">{language === 'es' ? 'Balance Plan' : 'Plan Balance'}</td>
              <td className="px-3 py-3 text-right">{language === 'es' ? 'Balance Real' : 'Real Balance'}</td>
              <td className="px-3 py-3 text-right">{language === 'es' ? 'Resultado (Real − Plan)' : 'Result (Real − Plan)'}</td>
              <td className="px-3 py-3 text-right">{language === 'es' ? 'Saldo Acumulado' : 'Accumulated'}</td>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {months.map((mName, m) => {
              const diff = real.balance[m] - plan.balance[m];
              acc += real.balance[m];
              const isOpen = selectedMonth === m;
              return (
                <React.Fragment key={mName}>
                  <tr
                    className={`cursor-pointer ${isOpen ? 'bg-indigo-50' : 'hover:bg-indigo-50/60'}`}
                    onClick={() => setSelectedMonth(isOpen ? null : m)}
                    data-testid={`fin-month-row-${m}`}
                  >
                    <td className="px-3 py-2 font-bold text-indigo-600">
                      <span className={`inline-block mr-1.5 text-[9px] transition-transform ${isOpen ? 'rotate-90' : ''}`}>▶</span>
                      {mName}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">${formatNumber(plan.balance[m])}</td>
                    <td className={`px-3 py-2 text-right font-bold ${real.balance[m] >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>${formatNumber(real.balance[m])}</td>
                    <td className={`px-3 py-2 text-right font-bold ${diff >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{diff >= 0 ? '+' : ''}${formatNumber(diff)}</td>
                    <td className={`px-3 py-2 text-right font-black ${acc >= 0 ? 'text-gray-800' : 'text-red-600'}`}>${formatNumber(acc)}</td>
                  </tr>
                  {isOpen && (
                    <tr data-testid={`fin-month-sub-${m}`}>
                      <td colSpan={5} className="p-0 bg-indigo-50/40">
                        <div className="p-2 sm:p-3">
                          <MonthDetail budget={budget} actual={actual} month={m} language={language} onBack={() => setSelectedMonth(null)} inline />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            <tr className="bg-gray-900 text-white font-black">
              <td className="px-3 py-2.5 uppercase">{language === 'es' ? 'Año' : 'Year'}</td>
              <td className="px-3 py-2.5 text-right">${formatNumber(plan.balance.reduce((a, b) => a + b, 0))}</td>
              <td className="px-3 py-2.5 text-right">${formatNumber(real.balance.reduce((a, b) => a + b, 0))}</td>
              <td className="px-3 py-2.5 text-right">${formatNumber(real.balance.reduce((a, b) => a + b, 0) - plan.balance.reduce((a, b) => a + b, 0))}</td>
              <td className="px-3 py-2.5 text-right">${formatNumber(acc)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function FinanzasPage({ financeData = {}, onFinanceChange, confirmedExtractions = {} }) {
  const { language } = useLanguage();
  const [view, setView] = useState('hub'); // hub | budget | actual | results
  const [year, setYear] = useState(new Date().getFullYear());

  const yearData = financeData[year] || {};
  const budget = yearData.budget || defaultTable();
  const actual = yearData.actual || defaultTable();

  const saveTable = (kind, table) => {
    onFinanceChange({
      ...financeData,
      [year]: { ...yearData, budget: kind === 'budget' ? table : (yearData.budget || budget), actual: kind === 'actual' ? table : (yearData.actual || actual) }
    });
  };

  const exportCSV = (table, name) => {
    const months = language === 'es' ? MONTHS_ES : MONTHS_EN;
    const lines = [['Concepto', ...months, 'AÑO'].join(',')];
    SECTIONS.forEach(s => {
      lines.push(language === 'es' ? s.es : s.en);
      (table[s.key] || []).forEach(r => lines.push([`"${r.name}"`, ...r.values, rowTotal(r)].join(',')));
    });
    const totals = computeTotals(table);
    lines.push(['BALANCE', ...totals.balance, totals.balance.reduce((a, b) => a + b, 0)].join(','));
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${name}_${year}.csv`;
    a.click();
  };

  const YearNav = () => (
    <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl px-2 py-1.5 shadow-sm">
      <button onClick={() => setYear(year - 1)} className="p-1 hover:bg-gray-100 rounded-lg" data-testid="fin-year-prev"><ChevronLeft size={16} /></button>
      <span className="font-black text-gray-800 px-2" data-testid="fin-year-label">{year}</span>
      <button onClick={() => setYear(year + 1)} className="p-1 hover:bg-gray-100 rounded-lg" data-testid="fin-year-next"><ChevronRight size={16} /></button>
    </div>
  );

  const NavBtns = ({ current }) => (
    <div className="flex items-center gap-2 flex-wrap">
      {current !== 'budget' && (
        <button onClick={() => setView('budget')} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-bold text-indigo-600 hover:bg-indigo-50 shadow-sm" data-testid="fin-goto-budget">
          <ClipboardList size={14} /> {language === 'es' ? 'Presupuesto' : 'Budget'}
        </button>
      )}
      {current !== 'actual' && (
        <button onClick={() => setView('actual')} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-bold text-emerald-600 hover:bg-emerald-50 shadow-sm" data-testid="fin-goto-actual">
          <Receipt size={14} /> {language === 'es' ? 'Gastos Reales' : 'Actual Expenses'}
        </button>
      )}
      {current !== 'results' && (
        <button onClick={() => setView('results')} className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-bold text-amber-600 hover:bg-amber-50 shadow-sm" data-testid="fin-goto-results">
          <BarChart3 size={14} /> {language === 'es' ? 'Resultados y Récord' : 'Results & Record'}
        </button>
      )}
      {current === 'budget' && (
        <button onClick={() => exportCSV(budget, 'presupuesto')} className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-sm" data-testid="fin-csv-btn">
          <Download size={14} /> CSV
        </button>
      )}
      {current === 'actual' && (
        <button onClick={() => exportCSV(actual, 'gastos_reales')} className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-sm" data-testid="fin-csv-btn">
          <Download size={14} /> CSV
        </button>
      )}
      <YearNav />
    </div>
  );

  if (view === 'hub') {
    const cards = [
      { v: 'budget', icon: ClipboardList, iconCls: 'bg-indigo-100 text-indigo-600', title: language === 'es' ? 'Presupuesto' : 'Budget', desc: language === 'es' ? 'Planifica tus ingresos y gastos del mes y cópialos al resto del año. Ajústalos cuando cambien.' : 'Plan your monthly income and expenses and copy them to the rest of the year.' },
      { v: 'actual', icon: Receipt, iconCls: 'bg-emerald-100 text-emerald-600', title: language === 'es' ? 'Gastos Reales' : 'Actual Expenses', desc: language === 'es' ? 'Registra lo que realmente ingresaste y gastaste cada mes, incluidas tus extracciones de trading.' : 'Record what you actually earned and spent each month, including your trading extractions.' },
      { v: 'results', icon: BarChart3, iconCls: 'bg-amber-100 text-amber-600', title: language === 'es' ? 'Resultados y Récord' : 'Results & Record', desc: language === 'es' ? 'Compara plan vs real: resultado positivo o negativo por mes y por año, con saldo acumulado.' : 'Compare plan vs real: positive or negative result per month and year, with accumulated balance.' }
    ];
    return (
      <div className="space-y-6" data-testid="finanzas-hub">
        <div className="flex items-center justify-between bg-white p-5 sm:p-6 rounded-xl shadow-sm border border-gray-100">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="text-indigo-600" /> {language === 'es' ? 'Gestión de Presupuesto' : 'Budget Management'}
          </h2>
          <YearNav />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {cards.map(c => (
            <button key={c.v} onClick={() => setView(c.v)} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-left hover:shadow-lg hover:-translate-y-0.5 transition-all" data-testid={`fin-card-${c.v}`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${c.iconCls}`}>
                <c.icon size={22} />
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-1">{c.title}</h3>
              <p className="text-sm text-gray-500">{c.desc}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  const titles = { budget: language === 'es' ? 'Presupuesto' : 'Budget', actual: language === 'es' ? 'Gastos Reales' : 'Actual Expenses', results: language === 'es' ? 'Resultados y Récord' : 'Results & Record' };
  const tips = {
    budget: language === 'es' ? '💡 Introduce el importe de un mes en una línea y usa el icono de copiar junto al nombre para replicarlo a todos los meses. Puedes renombrar líneas y agregar nuevas con +.' : '💡 Enter an amount in one month and use the copy icon next to the name to replicate it to all months.',
    actual: language === 'es' ? '💡 Registra aquí los importes reales de cada mes. Usa ⚡ en Ingresos Extras para traer tus extracciones de la app.' : '💡 Record your real amounts here. Use ⚡ on Extra Income to import your extractions.',
    results: language === 'es' ? '💡 Compara tu plan contra la realidad mes a mes. Verde = mejor que el plan.' : '💡 Compare your plan against reality month by month.'
  };

  return (
    <div className="space-y-4" data-testid={`finanzas-${view}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-4 sm:p-5 rounded-xl shadow-sm border border-gray-100">
        <div className="flex items-center gap-3">
          <button onClick={() => setView('hub')} className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-50" data-testid="fin-back-btn">
            <ArrowLeft size={15} /> {language === 'es' ? 'Volver' : 'Back'}
          </button>
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="text-indigo-600" size={22} /> {titles[view]}
          </h2>
        </div>
        <NavBtns current={view} />
      </div>
      <div className="bg-white px-4 py-3 rounded-xl border border-gray-100 text-xs sm:text-sm text-gray-500">{tips[view]}</div>
      {view === 'budget' && <BudgetGrid mode="budget" table={budget} onChange={(t) => saveTable('budget', t)} year={year} language={language} />}
      {view === 'actual' && <BudgetGrid mode="actual" table={actual} onChange={(t) => saveTable('actual', t)} year={year} confirmedExtractions={confirmedExtractions} language={language} />}
      {view === 'results' && <ResultsView budget={budget} actual={actual} language={language} />}
    </div>
  );
}
